import supabase from '../supabase/supabase';

const CUSTOMER_TABLE = 'customers';

const CUSTOMER_FIELDS = [
  'name',
  'company_name',
  'contact_name',
  'email',
  'phone',
  'customer_number',
  'currency',
  'payment_terms_days',
  'tax_id',
  'website',
  'notes',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'country',
  'is_active'
];

function requireOrganizationId(organizationId) {
  if (!organizationId) {
    throw new Error('Please select a client organization.');
  }
}

function requireCustomerId(customerId) {
  if (!customerId) {
    throw new Error('Customer ID is required.');
  }
}

function cleanText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeCustomerData(input = {}) {
  const data = {};

  for (const field of CUSTOMER_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(input, field)) {
      continue;
    }

    const value = input[field];

    if (field === 'is_active') {
      if (typeof value !== 'boolean') {
        throw new Error('Customer status must be active or inactive.');
      }

      data[field] = value;
      continue;
    }

    if (field === 'payment_terms_days') {
      const days = Number(value);

      if (
        !Number.isInteger(days) ||
        days < 0 ||
        days > 365
      ) {
        throw new Error(
          'Payment terms must be between 0 and 365 days.'
        );
      }

      data[field] = days;
      continue;
    }

    if (field === 'customer_number') {
      data[field] = cleanText(value) || null;
      continue;
    }

    if (field === 'currency') {
      const currency = cleanText(value).toUpperCase();

      if (!/^[A-Z]{3}$/.test(currency)) {
        throw new Error('Please enter a valid 3-letter currency code.');
      }

      data[field] = currency;
      continue;
    }

    if (field === 'country') {
      const country = cleanText(value).toUpperCase();

      if (country && !/^[A-Z]{2}$/.test(country)) {
        throw new Error('Country must be a 2-letter country code.');
      }

      data[field] = country || 'US';
      continue;
    }

    data[field] = cleanText(value);
  }

  if (
    Object.prototype.hasOwnProperty.call(data, 'name') &&
    !data.name
  ) {
    throw new Error('Customer name is required.');
  }

  if (
    data.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)
  ) {
    throw new Error('Please enter a valid email address.');
  }

  return data;
}

function handleDatabaseError(error, operation) {
  console.error(`CustomerService [${operation}]:`, error);

  if (error?.code === '23505') {
    throw new Error(
      'A customer with this customer number already exists in this organization.'
    );
  }

  if (error?.code === '23503') {
    throw new Error(
      'This customer is referenced by another accounting record.'
    );
  }

  throw new Error(
    error?.message || `Unable to ${operation} customer.`
  );
}

class CustomerService {
  /**
   * Get customers belonging to a selected client organization.
   *
   * By default, returns active and inactive customers.
   */
  async getCustomers(organizationId, options = {}) {
    requireOrganizationId(organizationId);

    const {
      activeOnly = false,
      includeInactive = true
    } = options;

    let query = supabase
      .schema('accounting')
      .from(CUSTOMER_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .order('name', { ascending: true });

    if (activeOnly || !includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) {
      handleDatabaseError(error, 'load');
    }

    return data || [];
  }

  /**
   * Get one customer by ID, scoped to its organization.
   */
  async getCustomerById(organizationId, customerId) {
    requireOrganizationId(organizationId);
    requireCustomerId(customerId);

    const { data, error } = await supabase
      .schema('accounting')
      .from(CUSTOMER_TABLE)
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', customerId)
      .maybeSingle();

    if (error) {
      handleDatabaseError(error, 'load');
    }

    return data;
  }

  /**
   * Create a customer for a client organization.
   *
   * Stripe customer creation is intentionally not performed here.
   * The existing invoice workflow manages Stripe synchronization.
   */
  async createCustomer(customerData, organizationId) {
    requireOrganizationId(organizationId);

    const normalized = normalizeCustomerData(customerData);

    if (!normalized.name) {
      throw new Error('Customer name is required.');
    }

    const payload = {
      organization_id: organizationId,
      ...normalized,
      email: normalized.email ?? '',
      phone: normalized.phone ?? '',
      company_name: normalized.company_name ?? '',
      currency: normalized.currency ?? 'USD',
      country: normalized.country ?? 'US',
      is_active: normalized.is_active ?? true
    };

    const { data, error } = await supabase
      .schema('accounting')
      .from(CUSTOMER_TABLE)
      .insert(payload)
      .select()
      .single();

    if (error) {
      handleDatabaseError(error, 'create');
    }

    return data;
  }

  /**
   * Update customer information.
   *
   * The organization filter prevents cross-organization updates.
   * Stripe IDs and ownership fields cannot be changed through
   * this method.
   */
  async updateCustomer(organizationId, customerId, customerData) {
    requireOrganizationId(organizationId);
    requireCustomerId(customerId);

    const payload = normalizeCustomerData(customerData);

    if (Object.keys(payload).length === 0) {
      throw new Error('No customer changes were provided.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from(CUSTOMER_TABLE)
      .update(payload)
      .eq('organization_id', organizationId)
      .eq('id', customerId)
      .select()
      .maybeSingle();

    if (error) {
      handleDatabaseError(error, 'update');
    }

    if (!data) {
      throw new Error(
        'Customer not found or you do not have permission to update it.'
      );
    }

    return data;
  }

  /**
   * Deactivate a customer without deleting their accounting history.
   */
  async deactivateCustomer(organizationId, customerId) {
    return this.updateCustomer(
      organizationId,
      customerId,
      { is_active: false }
    );
  }

  /**
   * Reactivate an existing customer.
   */
  async reactivateCustomer(organizationId, customerId) {
    return this.updateCustomer(
      organizationId,
      customerId,
      { is_active: true }
    );
  }

  /**
   * Compatibility method for customer removal.
   *
   * This intentionally archives the customer instead of
   * physically deleting the database record.
   */
  async deleteCustomer(organizationId, customerId) {
    return this.deactivateCustomer(
      organizationId,
      customerId
    );
  }

  /**
   * Retrieve invoices associated with a customer.
   */
  async getCustomerInvoices(organizationId, customerId) {
    requireOrganizationId(organizationId);
    requireCustomerId(customerId);

    const { data, error } = await supabase
      .schema('accounting')
      .from('invoices')
      .select(`
        id,
        invoice_number,
        customer_id,
        organization_id,
        status,
        currency,
        total_amount,
        amount_paid,
        amount_due,
        issue_date,
        due_date,
        paid_at,
        hosted_invoice_url,
        invoice_pdf_url,
        created_at
      `)
      .eq('organization_id', organizationId)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) {
      handleDatabaseError(error, 'load invoices for');
    }

    return data || [];
  }

  /**
   * Calculate customer receivables from existing invoice records.
   *
   * Amounts are stored in minor currency units (cents for USD).
   * Currency totals must not be mixed.
   */
  calculateCustomerSummary(invoices = []) {
    const summaries = {};

    const excludedStatuses = new Set([
      'draft',
      'void',
      'voided',
      'uncollectible',
      'deleted'
    ]);

    const unpaidStatuses = new Set([
      'open',
      'sent',
      'unpaid',
      'overdue',
      'partially_paid'
    ]);

    const today = new Date().toISOString().slice(0, 10);

    for (const invoice of invoices) {
      const currency = (
        invoice.currency || 'USD'
      ).toUpperCase();

      if (!summaries[currency]) {
        summaries[currency] = {
          currency,
          totalInvoiced: 0,
          totalPaid: 0,
          outstanding: 0,
          overdue: 0,
          invoiceCount: 0
        };
      }

      const summary = summaries[currency];
      const status = String(
        invoice.status || ''
      ).toLowerCase();

      if (excludedStatuses.has(status)) {
        continue;
      }

      const total = Math.max(
        0,
        Number(invoice.total_amount) || 0
      );

      const paid = Math.max(
        0,
        Number(invoice.amount_paid) || 0
      );

      const due = Math.max(
        0,
        Number(invoice.amount_due) || 0
      );

      summary.invoiceCount += 1;
      summary.totalInvoiced += total;
      summary.totalPaid += paid;

      if (unpaidStatuses.has(status)) {
        summary.outstanding += due;

        if (
          invoice.due_date &&
          invoice.due_date < today &&
          due > 0
        ) {
          summary.overdue += due;
        }
      }
    }

    return Object.values(summaries);
  }
}

export const customerService = new CustomerService();